import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getManagers } from '../../api/managerApi';
import '../../css/users.css';

function UserList() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [useMailEnabled, setUseMailEnabled] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedComCode, setSelectedComCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useContext(AuthContext);

  // 하위 페이지에서 돌아올 때 M_ComCode 복원
  useEffect(() => {
    const comCode = location.state?.M_ComCode;
    if (comCode) setSelectedComCode(comCode);
  }, [location.state]);

  // 전체 회사 목록 로드
  useEffect(() => {
    getManagers(token)
      .then(data => setCompanies(data))
      .catch(err => console.error('회사 목록 조회 실패:', err));
  }, [token]);

  const fetchUsers = useCallback(async (comCode) => {
    if (!comCode) return;
    try {
      const res = await axios.post('/api/auth/users', { M_ComCode: comCode }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch (err) {
      console.error('회원 목록 요청 실패:', err);
    }
  }, [token]);

  const handleComCodeChange = async (e) => {
    const comCode = e.target.value;
    setSelectedComCode(comCode);
    setUsers([]);
  };

  const handleDelete = async (id) => {
    try {
      if (window.confirm('정말 삭제하시겠습니까?')) {
        await axios.delete(`/api/auth/users/${id}`, {
          headers: { Authorization: `Bearer ${token}`, 'x-com-code': selectedComCode },
        });
        fetchUsers(selectedComCode);
      }
    } catch (err) {
      console.error('회원 삭제 실패:', err);
      alert(err.response?.data?.message || '삭제 중 오류 발생');
    }
  };

  const handleCreateMailbox = async (id) => {
    try {
      const res = await axios.post(`/api/mail/create/${id}`,{},
        {headers: {Authorization: `Bearer ${token}`, 'x-com-code': selectedComCode},}
      );
      console.log(res.data);
      alert("메일 생성 완료");
    } catch (err) {
      console.error("메일 생성 오류:", err);
      alert("메일 생성 실패");
    }
  };  
  
  const handleDeleteMailbox = async (id) => {
    try {
      if (window.confirm("정말 메일을 삭제하시겠습니까?")) {
        const res = await axios.delete(`/api/mail/delete/${id}`, {
          headers: { Authorization: `Bearer ${token}`, 'x-com-code': selectedComCode },
        });
        console.log(res.data);
        alert("메일 삭제 완료");
      }
    } catch (err) {
      console.error("메일 삭제 오류:", err);
      alert("메일 삭제 실패");
    }
  };
  
  const handleRegisterSso = async (userid) => {
    try {
      const res = await axios.post(
        `/api/auth/authentik/register-sso/${userid}`,{},
        { headers: { Authorization: `Bearer ${token}`, 'x-com-code': selectedComCode } }
      );
      console.log(res.data);
      alert("SSO 계정 생성 완료");
    } catch (err) {
      console.error("SSO 계정 생성 오류:", err);
      alert(err.response?.data?.message || "SSO 계정 생성 실패");
    }
  };

  useEffect(() => {
    if (selectedComCode) fetchUsers(selectedComCode);
  }, [fetchUsers, selectedComCode]);

  // 회사 선택 시 메일 사용 여부 조회 (복원 시에도 동작)
  useEffect(() => {
    if (!selectedComCode) {
      setUseMailEnabled(false);
      return;
    }
    axios.get(`/api/managers/${selectedComCode}`)
      .then(res => setUseMailEnabled(res.data?.M_UseMail === 1 || res.data?.M_UseMail === true))
      .catch(err => console.error('manager 정보 조회 실패:', err));
  }, [selectedComCode]);

  // 메일 useEffect 제거 (handleComCodeChange에서 처리)

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert('CSV 파일을 선택하세요');
      return;
    }

    const comCode = String(selectedComCode || '').trim();
    if (!comCode) {
      alert('회사를 먼저 선택하세요');
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        for (const row of result.data) {
          try {
            const payload = { ...row, M_ComCode: comCode };
            const exists = users.find(u => u.userid === row.userid);
            if (exists) {
              await axios.put(`/api/auth/users/${exists.id}`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
              });
            } else {
              await axios.post('/api/auth/register', payload, {
                headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
              });
            }
          } catch (err) {
            console.error('CSV 처리 오류:', err);
          }
        }
        fetchUsers(comCode);
        alert('CSV 업로드 완료!');
      },
    });
  };
  

  // ✅ 검색어로 필터링된 사용자 목록
  const filteredUsers = users.filter(
    (u) =>
      u.userid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">

          {/* 헤더 영역 */}
          <div className="d-flex justify-content-between align-items-center mb-3 users-header">
            <div>
              <h4 className="mb-0">👥 사용자 목록</h4>
            </div>
            {/* 회사 선택 콤보박스 */}
            <div>
              <select
                className="form-select"
                style={{ minWidth: '180px' }}
                value={selectedComCode}
                onChange={handleComCodeChange}
              >
                <option value="">회사를 선택해주세요.</option>
                {companies.map(c => (
                  <option key={c.M_ComCode} value={c.M_ComCode}>{c.M_ComName}</option>
                ))}
              </select>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button
                className="btn btn-success"
                disabled={!selectedComCode}
                onClick={() => navigate('/users/create', { state: { M_ComCode: selectedComCode, M_ComName: companies.find(c => c.M_ComCode === selectedComCode)?.M_ComName || '' } })}
              >
                + 사용자 등록
              </button>

              {/* ✅ CSV 업로드 input + 버튼 */}
              <input
                type="file"
                accept=".csv"
                className="form-control"
                style={{ maxWidth: '200px' }}
                disabled={!selectedComCode}
                onChange={(e) => setCsvFile(e.target.files[0])}
              />
              <button
                className="btn btn-primary"
                disabled={!selectedComCode}
                onClick={handleCsvUpload}
              >
                📤 CSV 업로드
              </button>
              <a
                href="/sampleFile/userSample.csv"
                download="deptSample.csv"
                className="btn btn-info"
              >
                📥 샘플 다운로드
              </a>

            </div>
          </div>

          {/* 검색 폼 */}
          <form
            className="row g-2 mb-3 users-search-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="col-md-6">
              <input
                type="text"
                name="search"
                className="form-control"
                placeholder="아이디 또는 이름 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm('')}
              >
                초기화
              </button>
            </div>
          </form>

          {/* 회원 목록 */}
          {!selectedComCode ? (
            <div className="users-empty">
              <p>회사를 선택해주세요.</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="users-empty">
              <div className="spinner-border text-secondary" role="status" />
              <p>사용자가 없습니다.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-nowrap align-middle table-hover mb-0 users-table">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '10%' }}>ID</th>
                    <th style={{ width: '20%' }}>아이디</th>
                    <th style={{ width: '20%' }}>이름</th>
                    <th style={{ width: '20%' }}>권한</th>
                    <th style={{ width: '30%' }} className="text-center">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/users/${u.id}`, { state: { M_ComCode: selectedComCode, M_ComName: companies.find(c => c.M_ComCode === selectedComCode)?.M_ComName || '' } })}
                      >
                        <h5 className="font-size-14 mb-0">{u.userid}</h5>
                      </td>
                      <td>{u.name}</td>
                      <td>{u.role}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-warning btn-sm me-2"
                          onClick={() => navigate(`/users/edit/${u.id}`, { state: { M_ComCode: selectedComCode, M_ComName: companies.find(c => c.M_ComCode === selectedComCode)?.M_ComName || '' } })}
                        >
                          수정
                        </button>
                        <button
                          className="btn btn-danger btn-sm me-2"
                          onClick={() => handleDelete(u.id)}
                        >
                          삭제
                        </button>
                        {/* ✅ M_UseMail이 활성화된 경우에만 메일 버튼 표시 */}
                        {useMailEnabled && u.maildb === 0 && (
                            <button
                              className="btn btn-info btn-sm me-2"
                              onClick={() => handleCreateMailbox(u.id)}
                            >
                              메일함 생성
                            </button>
                          )}

                          {useMailEnabled && u.maildb === 1 && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDeleteMailbox(u.id)}
                            >
                              메일함 삭제
                            </button>
                          )}
                          {useMailEnabled && u.maildb === 1 && u.sso_yn === 0 && (
                            <button
                              className="btn btn-warning btn-sm ms-2"
                              onClick={() => handleRegisterSso(u.userid)}
                            >
                              SSO 생성
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserList;